import { ActionType } from '@unifyapps/defs/types/action';
import type { BlockStateUnionType, BlockType } from '@unifyapps/defs/types/block';
import type { ApplicationType } from '@unifyapps/defs/types/interface';
import type { OnActionType } from '../../../context/ActionsProvider/context';
import type BlocksRegistry from '../../../context/RegistryProvider/BlocksRegistry';
import type { BlockControlMethod } from '../../../types/blockDefinition';
import { getOriginalBlockId, isRuntimeBlockId } from '../../../utils/runTimeBlocks';
import { CM_SCOPE_SCHEMA, type ScopeSchemaNode } from '../../../utils/scopeSchema';
import { methodSchemaToPayloadTsType } from './jsonSchemaToTsType';

const isWarningLogged: Record<string, boolean> = {};

function stampSchema(fn: (...args: unknown[]) => unknown, schema: ScopeSchemaNode): void {
  (fn as unknown as Record<symbol, unknown>)[CM_SCOPE_SCHEMA] = schema;
}

const createBlockMethodFunction = (
  blockId: string,
  targetBlockId: string,
  methodIds: string[],
  onAction?: OnActionType,
  instanceId?: string,
) => {
  if (methodIds.length === 0) {
    return {};
  }

  const methodFunctions: Record<string, (methodPayload: Record<string, unknown>) => void> = {};

  for (let methodIndex = 0; methodIndex < methodIds.length; methodIndex++) {
    const methodId = methodIds[methodIndex];
    methodFunctions[methodId] = (methodPayload: Record<string, unknown>) => {
      if (!onAction) return;

      void onAction({
        blockId: targetBlockId,
        action: {
          actionType: ActionType.ControlBlockMethod,
          payload: {
            blockId,
            methodName: methodId,
            methodPayload,
          },
        },
        actionContext: {},
        instanceId,
      });
    };
  }

  return methodFunctions;
};

const createDataSourceMethodFunction = (
  dataSourceId: string,
  methodName: string,
  onAction?: OnActionType,
  instanceId?: string,
) => {
  const methodFunction = () => {
    if (!onAction) return;

    void onAction({
      action: {
        actionType: ActionType.ControlDataSource,
        payload: {
          method: methodName,
          dataSourceId,
        },
      },
      actionContext: {},
      instanceId,
    });
  };

  stampSchema(methodFunction, { kind: 'function', params: [], returnType: 'void' });
  return methodFunction;
};

const createPageVariableMethodFunction = (
  pageVariableId: string,
  _methodName: string,
  onAction?: OnActionType,
  instanceId?: string,
) => {
  const methodFunction = (value: unknown) => {
    if (!onAction) return;

    void onAction({
      action: {
        actionType: ActionType.SetPageVariable,
        payload: {
          variableId: pageVariableId,
          operationDetails: {
            operation: 'SET',
            value,
          },
        },
      },
      actionContext: {},
      instanceId,
    });
  };

  stampSchema(methodFunction as (...args: unknown[]) => unknown, {
    kind: 'function',
    params: [{ name: 'value', type: 'unknown' }],
    returnType: 'void',
  });
  return methodFunction;
};

const createLocalStorageMethodFunction = (
  methodName: 'setValue' | 'clear',
  onAction?: OnActionType,
  instanceId?: string,
) => {
  if (methodName === 'setValue') {
    const fn = (key: string, value: unknown) => {
      if (!onAction) return;

      void onAction({
        action: {
          actionType: ActionType.SetLocalStorage,
          payload: {
            operation: 'SET',
            key,
            value,
          },
        },
        actionContext: {},
        instanceId,
      });
    };
    stampSchema(fn as (...args: unknown[]) => unknown, {
      kind: 'function',
      params: [
        { name: 'key', type: 'string' },
        { name: 'value', type: 'unknown' },
      ],
      returnType: 'void',
    });
    return fn;
  }

  const fn = () => {
    if (!onAction) return;

    void onAction({
      action: {
        actionType: ActionType.SetLocalStorage,
        payload: {
          operation: 'CLEAR',
        },
      },
      actionContext: {},
      instanceId,
    });
  };
  stampSchema(fn, { kind: 'function', params: [], returnType: 'void' });
  return fn;
};

const createSessionStorageMethodFunction = (
  methodName: 'setValue' | 'clear',
  onAction?: OnActionType,
  instanceId?: string,
) => {
  if (methodName === 'setValue') {
    const fn = (key: string, value: unknown) => {
      if (!onAction) return;

      void onAction({
        action: {
          actionType: ActionType.SetInterfaceSessionStorage,
          payload: {
            operation: 'SET',
            key,
            value,
          },
        },
        actionContext: {},
        instanceId,
      });
    };
    stampSchema(fn as (...args: unknown[]) => unknown, {
      kind: 'function',
      params: [
        { name: 'key', type: 'string' },
        { name: 'value', type: 'unknown' },
      ],
      returnType: 'void',
    });
    return fn;
  }

  const fn = () => {
    if (!onAction) return;

    void onAction({
      action: {
        actionType: ActionType.SetInterfaceSessionStorage,
        payload: {
          operation: 'CLEAR',
        },
      },
      actionContext: {},
      instanceId,
    });
  };
  stampSchema(fn, { kind: 'function', params: [], returnType: 'void' });
  return fn;
};

export const getLocalStorageMethods = ({
  onAction,
  instanceId,
}: {
  onAction?: OnActionType;
  instanceId?: string;
}) => {
  return {
    setValue: createLocalStorageMethodFunction('setValue', onAction, instanceId),
    clear: createLocalStorageMethodFunction('clear', onAction, instanceId),
  };
};

export const getSessionStorageMethods = ({
  onAction,
  instanceId,
}: {
  onAction?: OnActionType;
  instanceId?: string;
}) => {
  return {
    setValue: createSessionStorageMethodFunction('setValue', onAction, instanceId),
    clear: createSessionStorageMethodFunction('clear', onAction, instanceId),
  };
};

export const getPageVariablesMethods = ({
  pageVariableIds,
  onAction,
  instanceId,
}: {
  pageVariableIds: string[];
  onAction?: OnActionType;
  instanceId?: string;
}) => {
  const pageVariablesMethods = {};

  for (let pageVariableIndex = 0; pageVariableIndex < pageVariableIds.length; pageVariableIndex++) {
    const pageVariableId = pageVariableIds[pageVariableIndex];
    pageVariablesMethods[pageVariableId] = {
      setValue: createPageVariableMethodFunction(pageVariableId, 'setValue', onAction, instanceId),
    };
  }

  return pageVariablesMethods;
};

export const getBlocksMethods = ({
  blocks,
  blockStates,
  onAction,
  blockRegistry,
  applicationType,
  instanceId,
}: {
  blockStates: Record<string, BlockStateUnionType | undefined>;
  blocks: Record<string, BlockType | undefined>;
  onAction?: OnActionType;
  blockRegistry: BlocksRegistry;
  applicationType: ApplicationType | undefined;
  instanceId?: string;
}): Record<string, BlockControlMethod[] | undefined> => {
  const result = {};
  // contains runtimeblock ids and normal block ids
  const runtimeOrOriginalBlockIds = Object.keys(blockStates);

  for (let blockIndex = 0; blockIndex < runtimeOrOriginalBlockIds.length; blockIndex++) {
    const runtimeOrOriginalBlockId = runtimeOrOriginalBlockIds[blockIndex];
    const blockState = blockStates[runtimeOrOriginalBlockId];
    if (!blockState) continue;

    const isRuntimeBlock = isRuntimeBlockId(runtimeOrOriginalBlockId);
    const originalBlockId = getOriginalBlockId(runtimeOrOriginalBlockId);

    // if it is runTimeBlock then we don't have componentType stored with blockState
    const componentType = isRuntimeBlock
      ? blockStates[originalBlockId]?.componentType
      : blockState?.componentType;

    if (!componentType) {
      const warningMessage = `Component type is not defined for block ${runtimeOrOriginalBlockId}`;
      if (!isWarningLogged[warningMessage]) {
        console.warn(warningMessage);
        isWarningLogged[warningMessage] = true;
      }
      continue;
    }

    const originalBlockConfig = blocks[originalBlockId];

    if (!originalBlockConfig) {
      const warningMessage = `Block not found in blocks ${runtimeOrOriginalBlockId}`;
      if (!isWarningLogged[warningMessage]) {
        console.warn(warningMessage);
        isWarningLogged[warningMessage] = true;
      }
      continue;
    }

    const methods = blockRegistry.getBlockControlMethods(
      componentType,
      originalBlockConfig,
      applicationType,
    ) as BlockControlMethod[] | undefined;

    if (!methods) {
      const debugMessage = `Methods are not defined for block ${runtimeOrOriginalBlockId}`;
      if (!isWarningLogged[debugMessage]) {
        console.debug(debugMessage);
        isWarningLogged[debugMessage] = true;
      }
      continue;
    }

    const methodIds: string[] = [];

    for (let methodIndex = 0; methodIndex < methods.length; methodIndex++) {
      methodIds.push(methods[methodIndex].id);
    }

    const blockFns = createBlockMethodFunction(
      originalBlockId,
      runtimeOrOriginalBlockId,
      methodIds,
      onAction,
      instanceId,
    );
    result[runtimeOrOriginalBlockId] = blockFns;

    const editorDefsPresent = blockRegistry.hasBlockEditorDefinitions();

    if (editorDefsPresent) {
      for (const method of methods) {
        const fn = (blockFns as Record<string, unknown>)[method.id];
        if (typeof fn !== 'function') continue;

        try {
          const methodSchema = blockRegistry.getBlockMethodSchema(originalBlockConfig, method.id);
          const payloadType = methodSchema
            ? methodSchemaToPayloadTsType(methodSchema.schema)
            : null;
          stampSchema(fn as (...args: unknown[]) => unknown, {
            kind: 'function',
            params: payloadType ? [{ name: 'methodPayload', type: payloadType }] : [],
            returnType: 'void',
          });
        } catch {
          // schema lookup failure must not break method execution
        }
      }
    }
  }

  return result;
};

export const getDataSourcesMethods = ({
  dataSourceIds,
  onAction,
  instanceId,
}: {
  dataSourceIds: string[];
  onAction?: OnActionType;
  instanceId?: string;
}) => {
  const result = {};

  for (let dataSourceIndex = 0; dataSourceIndex < dataSourceIds.length; dataSourceIndex++) {
    const dataSourceId = dataSourceIds[dataSourceIndex];
    result[dataSourceId] = {
      trigger: createDataSourceMethodFunction(dataSourceId, 'trigger', onAction, instanceId),
    };
  }
  return result;
};

const createPageFunctionMethodFunction = (
  pageFunctionId: string,
  onAction?: OnActionType,
  instanceId?: string,
  getComputeContext?: () => Record<string, unknown>,
) => {
  const methodFunction = () => {
    if (!onAction) return;

    void onAction({
      action: {
        actionType: ActionType.TriggerFunction,
        payload: {
          functionId: pageFunctionId,
        },
      },
      actionContext: getComputeContext?.() ?? {},
      instanceId,
    });
  };

  stampSchema(methodFunction, { kind: 'function', params: [], returnType: 'void' });
  return methodFunction;
};

export const getPageFunctionsMethods = ({
  pageFunctionIds,
  onAction,
  instanceId,
  getComputeContext,
}: {
  pageFunctionIds: string[];
  onAction?: OnActionType;
  instanceId?: string;
  getComputeContext?: () => Record<string, unknown>;
}) => {
  const pageFunctionsMethods = {};

  for (let pageFunctionIndex = 0; pageFunctionIndex < pageFunctionIds.length; pageFunctionIndex++) {
    const pageFunctionId = pageFunctionIds[pageFunctionIndex];
    pageFunctionsMethods[pageFunctionId] = {
      trigger: createPageFunctionMethodFunction(
        pageFunctionId,
        onAction,
        instanceId,
        getComputeContext,
      ),
    };
  }

  return pageFunctionsMethods;
};
